import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, ShoppingBag, Receipt, AlertTriangle, ArrowUp, ArrowDown, Boxes, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const StatCard = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="pav-card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold">{label}</p>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent || "bg-[#F4F6FB] text-[#0B1E3F]"}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <p className="font-display text-3xl font-extrabold text-[#1A1A1A] mt-3">{value}</p>
    {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
  </div>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || !data) return (
    <div className="p-12 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-[#0B1E3F]" /></div>
  );

  const maxRev = Math.max(1, ...data.sales_by_day.map((d) => d.revenue));

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Dashboard</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0B1E3F] mt-1">
            Welcome back, {user?.name?.split(" ")[0] || "Admin"}
          </h1>
          <p className="text-neutral-600 mt-1 text-sm">Business overview · last {days} days</p>
        </div>
        <div className="flex gap-1 bg-white border border-[#E2E8F0] rounded-lg p-1" data-testid="period-selector">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              data-testid={`period-${d}-button`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${days === d ? "bg-[#0B1E3F] text-white" : "text-neutral-500 hover:text-[#0B1E3F]"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Revenue" value={peso(data.revenue)} sub={`${data.orders.paid} paid orders`} icon={TrendingUp} accent="bg-[#0B1E3F] text-white" />
        <StatCard label="Expenses" value={peso(data.expenses)} sub="all categories" icon={Receipt} accent="bg-[#06B6D4] text-white" />
        <StatCard
          label="Net Profit"
          value={peso(data.profit)}
          sub={data.profit >= 0 ? "in the green" : "in the red"}
          icon={data.profit >= 0 ? ArrowUp : ArrowDown}
          accent={data.profit >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
        />
        <StatCard label="Orders" value={data.orders.total} sub={`${data.orders.online} online · ${data.orders.manual} manual`} icon={ShoppingBag} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 pav-card">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-lg font-bold">Revenue trend</h2>
            <span className="text-xs text-neutral-500">last {days} days</span>
          </div>
          {data.sales_by_day.length === 0 ? (
            <p className="text-sm text-neutral-500 py-8 text-center">No paid orders in this period yet.</p>
          ) : (
            <div className="flex items-end gap-1 h-48 mt-4" data-testid="revenue-chart">
              {data.sales_by_day.map((d) => (
                <div key={d.date} className="flex-1 group relative" title={`${d.date}: ${peso(d.revenue)} (${d.count} orders)`}>
                  <div className="bg-[#0B1E3F] hover:bg-[#06B6D4] rounded-t-md transition-colors"
                       style={{ height: `${(d.revenue / maxRev) * 100}%`, minHeight: "2px" }} />
                </div>
              ))}
            </div>
          )}
          {data.sales_by_day.length > 0 && (
            <div className="flex justify-between mt-2 text-[10px] text-neutral-400">
              <span>{data.sales_by_day[0]?.date}</span>
              <span>{data.sales_by_day[data.sales_by_day.length - 1]?.date}</span>
            </div>
          )}
        </div>

        <div className="pav-card">
          <h2 className="font-display text-lg font-bold mb-4">Expenses by category</h2>
          {data.expenses_by_category.length === 0 ? (
            <p className="text-sm text-neutral-500 py-8 text-center">No expenses recorded.</p>
          ) : (
            <ul className="space-y-3" data-testid="expenses-by-category">
              {data.expenses_by_category.map((e) => {
                const pct = data.expenses > 0 ? (e.amount / data.expenses) * 100 : 0;
                return (
                  <li key={e.category}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize text-neutral-700">{e.category}</span>
                      <span className="font-semibold">{peso(e.amount)}</span>
                    </div>
                    <div className="h-1.5 bg-[#F4F6FB] rounded-full overflow-hidden">
                      <div className="h-full bg-[#06B6D4]" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/admin/expenses" className="text-xs text-[#0B1E3F] font-semibold hover:underline mt-4 inline-block">
            Manage expenses →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="pav-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold">Top consumed supplies</h2>
            <Boxes className="h-5 w-5 text-neutral-400" />
          </div>
          {data.top_consumed_supplies.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">No consumption logged yet.</p>
          ) : (
            <ul className="divide-y divide-[#E2E8F0]" data-testid="top-supplies">
              {data.top_consumed_supplies.map((s) => (
                <li key={s.supply_id} className="flex justify-between py-2.5 text-sm">
                  <span>{s.name}</span>
                  <span className="font-semibold">{s.qty} {s.unit}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pav-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold">Low stock alerts</h2>
            <AlertTriangle className={`h-5 w-5 ${data.low_stock.length > 0 ? "text-red-500" : "text-neutral-400"}`} />
          </div>
          {data.low_stock.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">All supplies are above reorder level.</p>
          ) : (
            <ul className="divide-y divide-[#E2E8F0]" data-testid="low-stock-list">
              {data.low_stock.map((s) => (
                <li key={s.supply_id} className="flex justify-between py-2.5 text-sm">
                  <span>{s.name}</span>
                  <span className="text-red-600 font-semibold">{s.stock_qty} {s.unit} <span className="text-neutral-400">/ {s.reorder_level}</span></span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/admin/supplies" className="text-xs text-[#0B1E3F] font-semibold hover:underline mt-4 inline-block">
            Manage supplies →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
